import { z } from "zod";
import {
  ContentFitnessCoverageStatusSchema,
  ContentFitnessVerificationStateSchema,
} from "../../enums/content-fitness";
import { IssueSeveritySchema, IssueStatusSchema } from "../../enums/core";
import {
  ScrapeArtifactKindSchema,
  ScrapeCaptureVersionSchema,
  ScreenshotDeviceSchema,
} from "../../enums/scrape";
import { EvidenceKindSchema } from "../../enums/yeet";
import { MetricKeySchema } from "../../report/catalog";
import { ContentFitnessSubcriterionKeySchema } from "../../report/content-fitness/subcriteria";
import { JobStatusValueSchema } from "../job/responses";

export const PageDetailResponseSchema = z.object({
  page: z.object({
    id: z.uuid(),
    jobId: z.uuid(),
    url: z.url(),
    path: z.string(),
    title: z.string().nullable(),
    depth: z.number().int().min(0),
    humanScore: z.number().nullable(),
    agentScore: z.number().nullable(),
    mapX: z.number().nullable(),
    mapY: z.number().nullable(),
    contentPreview: z.string().nullable(),
    contentLength: z.number().int().min(0),
    createdAt: z.coerce.date(),
  }),
  job: z.object({
    id: z.uuid(),
    status: JobStatusValueSchema,
    startedAt: z.coerce.date().nullable(),
    finishedAt: z.coerce.date().nullable(),
  }),
  report: z.object({
    id: z.uuid(),
    pageCount: z.number().int().min(0),
    createdAt: z.coerce.date(),
  }),
  // Scrape-capture read surface. Built by the backend from
  // workers.scraped_page* joined through crawl_run.jobId. Null when the page
  // predates artifact capture or has no facts. Never exposes raw
  // HTML/cookies/full XHR bodies — only summaries and short samples.
  scrapeProof: z
    .object({
      captureVersion: ScrapeCaptureVersionSchema,
      requestedUrl: z.url(),
      resolvedUrl: z.url().nullable(),
      httpStatus: z.number().int().min(100).max(599),
      responseType: z.string().nullable(),
      capturedAt: z.coerce.date(),
      artifacts: z.array(
        z.object({
          kind: ScrapeArtifactKindSchema,
          blobKey: z.string(),
          sha256: z.string(),
          byteLength: z.number().int().min(0),
        }),
      ),
      summary: z.object({
        renderedBytes: z.number().int().min(0).nullable(),
        sourceBytes: z.number().int().min(0).nullable(),
        textLength: z.number().int().min(0),
        linkCount: z.number().int().min(0),
        jsonLdCount: z.number().int().min(0),
        jsonLdParseErrors: z.number().int().min(0),
        formCount: z.number().int().min(0),
        buttonCount: z.number().int().min(0),
        nextHydrationDetected: z.boolean(),
      }),
      samples: z.object({
        canonicalUrl: z.string().nullable(),
        metaDescription: z.string().nullable(),
        renderedText: z.string().nullable(),
        sourceText: z.string().nullable(),
        links: z.array(z.string()).max(10),
      }),
    })
    .nullable(),
  contentFitness: z
    .object({
      frameworkVersion: z.string(),
      overallScore: z.number().int().min(0).max(100).nullable(),
      overallCoverageStatus: ContentFitnessCoverageStatusSchema,
      assessedSubcriteria: z.number().int().min(0),
      canonicalSubcriteria: z.number().int().min(0),
      dimensions: z.array(
        z.object({
          key: MetricKeySchema,
          label: z.string(),
          score: z.number().int().min(0).max(100).nullable(),
          coverageStatus: ContentFitnessCoverageStatusSchema,
          assessedSubcriteria: z.number().int().min(0),
          canonicalSubcriteria: z.number().int().min(0),
        }),
      ),
      assessments: z.array(
        z.object({
          subcriterionKey: ContentFitnessSubcriterionKeySchema,
          parentMetricKey: MetricKeySchema,
          label: z.string(),
          verificationState: ContentFitnessVerificationStateSchema,
          score: z.number().int().min(0).max(4).nullable(),
          rationale: z.string(),
          evidence: z.array(
            z.object({ kind: EvidenceKindSchema, value: z.string() }),
          ),
        }),
      ),
    })
    .nullable(),
  // Promoted screenshot evidence for the page. Bytes are served by
  // GET /api/websites/:id/pages/:pageId/screenshots/:screenshotId.
  // Empty array is the deliberate "no screenshots" state.
  screenshots: z.array(
    z.object({
      id: z.uuid(),
      device: ScreenshotDeviceSchema,
      byteLength: z.number().int().min(0),
      capturedAt: z.coerce.date(),
    }),
  ),
  // Issues linked to this page through the page_issues many-to-many.
  // effectiveSeverity = severityOverride ?? issue severity.
  issues: z.array(
    z.object({
      id: z.uuid(),
      title: z.string(),
      metricKey: MetricKeySchema,
      primarySubcriterionKey: ContentFitnessSubcriterionKeySchema.nullable(),
      severity: IssueSeveritySchema,
      effectiveSeverity: IssueSeveritySchema,
      status: IssueStatusSchema,
      locator: z.string().nullable(),
      evidenceSnippet: z.string().nullable(),
    }),
  ),
});
export type PageDetailResponse = z.infer<typeof PageDetailResponseSchema>;
